<script setup lang="ts">
import type { PropertyOnPage } from '@@/shared/property/api-types';

const props = defineProps<{
  property: PropertyOnPage
  title: string
  variant: 'video' | 'virtual-tour'
}>();

const videoUrl = computed(() => {
  if (props.variant === 'virtual-tour') return props.property.virtualTours?.[0];
  return props.property.videos?.[0];
});

const isYoutubeVideo = computed(() => {
  if (!videoUrl.value) return false;
  const url = new URL(videoUrl.value);
  if (url.hostname !== `www.youtube.com` && url.hostname !== `m.youtube.com` && url.hostname !== `youtu.be`) return false;
  if (url.hostname === 'youtu.be') return url.pathname.length > 1;
  if (url.hostname === 'www.youtube.com' || url.hostname === 'm.youtube.com') return !!url.searchParams.get('v');
  return false;
});

const youtubeVideoId = computed(() => {
  if (!videoUrl.value) return null;
  const url = new URL(videoUrl.value);
  if (url.hostname === 'www.youtube.com' || url.hostname === 'm.youtube.com') return url.searchParams.get('v');
  if (url.hostname === 'youtu.be') return url.pathname.slice(1);
  return null;
});

const youtubeEmbedUrl = computed(() => `https://www.youtube.com/embed/${youtubeVideoId.value}`);
</script>

<template>
  <div class="flex flex-1">
    <iframe
      v-if="isYoutubeVideo"
      :src="youtubeEmbedUrl"
      frameborder="0"
      allowfullscreen
      class="h-[315px] md:h-[460px] lg:h-full w-full"
    />
    <a
      v-else-if="videoUrl"
      :href="videoUrl"
      target="_blank"
      class="relative flex flex-1"
    >
      <figure class="flex flex-1 relative before:content-[''] before:absolute before:top-0 before:left-0 before:w-full before:h-full before:bg-black before:opacity-25">
        <img
          src="/assets/images/placeholder-video.svg"
          :alt="`${title} — ${$t('property.gallery.video.count')}`"
          class="object-cover flex-1"
        >
      </figure>
      <span
        class="bg-black bg-opacity-50 text-white absolute bottom-xs left-xs px-xs rounded"
        v-text="variant === 'virtual-tour' ? $t('property.gallery.virtual_tour.watch') : $t('property.gallery.video.watch')"
      />
    </a>
  </div>
</template>
